import { Typography, type TypographyProps } from '@mui/material'
import React, { useMemo } from 'react'

import { TruncatedTextWithTooltip } from 'components/common/TruncatedTextWithTooltip'
import { NODE_TEXT_MAX_WIDTH } from 'components/scenarios/concurrent/concurrent-scenario-form-utils'
import {
  getExternalActionTargetPrefix,
  getExternalActionText
} from 'components/scenarios/utils/graph/messaging'
import {
  isExternalActionNode,
  isRollbackExternalActionNode
} from 'components/scenarios/utils/graph/nodes/typeguards'
import {
  type ExternalActionNode,
  ExternalActionRequestType,
  type RollbackExternalActionNode
} from 'types/api/scenarios/scenario'
import {
  getAWSAutoscalingGroupNameFromASGArn,
  getAWSLoadBalancerNameFromLoadBalancerArn
} from 'utils/status-checks/status-check-integrations-util-aws'

const TYPOGRAPHY_SPAN_PROPS = { component: 'span', variant: 'inherit' } as const

export const ExternalActionDescription = ({
  node,
  shouldDisplayResourceName = true,
  ...typographyWrapperProps
}: {
  node: ExternalActionNode | RollbackExternalActionNode
  shouldDisplayResourceName?: boolean
} & Omit<TypographyProps, 'children'>) => {
  const { externalActionRequest } = node

  const isDisablingAction =
    isExternalActionNode(node) &&
    (externalActionRequest.type ===
      ExternalActionRequestType.DisableAWSAutoScalingGroups ||
      externalActionRequest.type ===
        ExternalActionRequestType.RemoveAWSSecurityGroups)

  const actionColor = isDisablingAction ? 'error' : 'success.main'

  const { action, target, resourceName } = useMemo(
    () => ({
      action: isRollbackExternalActionNode(node)
        ? 'Re-enable'
        : getExternalActionText(externalActionRequest),
      target: getExternalActionTargetPrefix(
        externalActionRequest,
        shouldDisplayResourceName
      ),
      resourceName:
        externalActionRequest.type ===
        ExternalActionRequestType.RemoveAWSSecurityGroups
          ? getAWSLoadBalancerNameFromLoadBalancerArn(
              externalActionRequest.resourceArn
            )
          : getAWSAutoscalingGroupNameFromASGArn(
              externalActionRequest.resourceArn
            )
    }),
    [externalActionRequest, node, shouldDisplayResourceName]
  )

  return (
    <Typography
      sx={{
        maxWidth: NODE_TEXT_MAX_WIDTH
      }}
      variant="body1"
      {...typographyWrapperProps}
    >
      <Typography
        color={actionColor}
        fontWeight="bold"
        {...TYPOGRAPHY_SPAN_PROPS}
      >
        {action}
      </Typography>{' '}
      <Typography {...TYPOGRAPHY_SPAN_PROPS}>
        {target}{' '}
        {shouldDisplayResourceName && (
          <TruncatedTextWithTooltip
            fontWeight="bold"
            tooltipProps={{
              title: resourceName
            }}
            {...TYPOGRAPHY_SPAN_PROPS}
          >
            {resourceName}
          </TruncatedTextWithTooltip>
        )}
      </Typography>
    </Typography>
  )
}
